/**
 * Iter S · Stage 2 — Zelle Payment Verification card.
 *
 * Additive extension to the existing order detail page. Renders a compact
 * comparison of the order's customer and its Zelle payment reconciliation
 * fields, side-by-side. Admin can:
 *   - record the Zelle sender name (may differ from the customer name)
 *   - record the actual amount received
 *   - record when the payment landed (date/time)
 *   - record the Zelle confirmation / reference number
 *   - add payment notes
 *   - set a verification status
 *
 * The card never mutates the order total. The `difference` is derived on
 * the fly. When Amount Received differs from Order Total by any amount,
 * a soft non-alarming notice appears; the admin can still mark the payment
 * as "Matched" (spec: "the system should help the administrator recognize
 * the mismatch but allow the administrator to approve it").
 *
 * Persistence: PATCH /api/admin/orders/{public_id}/payment-reconciliation
 * with only the changed fields (before/after diff written to audit log).
 */
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { detailLong } from "@/lib/businessTime";


const STATUS_OPTIONS = [
  { value: "not_reviewed",                 label: "Not reviewed",                tone: "mute" },
  { value: "matched",                      label: "Matched",                     tone: "ok" },
  { value: "amount_differs",               label: "Amount differs",              tone: "warn" },
  { value: "sender_name_differs",          label: "Sender name differs",         tone: "warn" },
  { value: "needs_customer_confirmation",  label: "Needs customer confirmation", tone: "warn" },
];


function statusTone(v) {
  const s = STATUS_OPTIONS.find((o) => o.value === v);
  return s?.tone || "mute";
}

function toneClass(tone) {
  switch (tone) {
    case "ok":   return "border-emerald-700 bg-emerald-50 text-emerald-900";
    case "warn": return "border-amber-700 bg-amber-50 text-amber-900";
    default:     return "border-neutral-400 bg-neutral-50 text-neutral-700";
  }
}


/** "+$1.65" / "-$1.65" / "$0.00" with sign preserved. */
function formatDifference(diff) {
  if (diff === null || diff === undefined) return "—";
  const n = Number(diff);
  if (Number.isNaN(n)) return "—";
  if (n === 0) return "$0.00";
  const sign = n > 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}


function currency(amt) {
  if (amt === null || amt === undefined || amt === "") return "—";
  const n = Number(amt);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}


export default function ZellePaymentVerificationCard({ order, onSaved }) {
  const rec = order?.payment_reconciliation || {};
  const orderTotal = Number(rec.order_total || 0);
  const customerName = order?.customer?.name || "—";
  const customerEmail = order?.customer?.email || "—";

  // Editable local state — hydrated from server, reset when the underlying
  // order changes (e.g. after another admin action forces a reload).
  const [senderName, setSenderName] = useState(rec.zelle_sender_name || "");
  const [amountRcv, setAmountRcv] = useState(
    rec.zelle_amount_received === null || rec.zelle_amount_received === undefined
      ? "" : String(rec.zelle_amount_received),
  );
  const [receivedAt, setReceivedAt] = useState(rec.zelle_received_at || "");
  const [reference, setReference] = useState(rec.zelle_reference || "");
  const [notes, setNotes] = useState(rec.zelle_payment_notes || "");
  const [status, setStatus] = useState(rec.zelle_verification_status || "not_reviewed");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSenderName(rec.zelle_sender_name || "");
    setAmountRcv(
      rec.zelle_amount_received === null || rec.zelle_amount_received === undefined
        ? "" : String(rec.zelle_amount_received),
    );
    setReceivedAt(rec.zelle_received_at || "");
    setReference(rec.zelle_reference || "");
    setNotes(rec.zelle_payment_notes || "");
    setStatus(rec.zelle_verification_status || "not_reviewed");
  }, [order?.public_id, order?.updated_at, rec.zelle_sender_name, rec.zelle_amount_received, rec.zelle_received_at, rec.zelle_reference, rec.zelle_payment_notes, rec.zelle_verification_status]);

  // Live difference from the current input (not from the server value) so
  // the admin sees the sign flip as they type.
  const parsedAmt = amountRcv === "" ? null : Number(amountRcv);
  const liveDiff = useMemo(() => {
    if (parsedAmt === null || Number.isNaN(parsedAmt)) return null;
    return Math.round((parsedAmt - orderTotal) * 100) / 100;
  }, [parsedAmt, orderTotal]);

  const senderMismatch = !!(senderName && customerName && senderName.trim().toLowerCase() !== customerName.trim().toLowerCase());
  const amountMismatch = liveDiff !== null && liveDiff !== 0;

  const dirty = (
    (senderName || "") !== (rec.zelle_sender_name || "") ||
    (amountRcv || "") !== (rec.zelle_amount_received === null || rec.zelle_amount_received === undefined ? "" : String(rec.zelle_amount_received)) ||
    (receivedAt || "") !== (rec.zelle_received_at || "") ||
    (reference || "") !== (rec.zelle_reference || "") ||
    (notes || "") !== (rec.zelle_payment_notes || "") ||
    (status || "not_reviewed") !== (rec.zelle_verification_status || "not_reviewed")
  );

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        zelle_sender_name:         senderName.trim() || null,
        zelle_amount_received:     amountRcv === "" ? null : Number(amountRcv),
        zelle_received_at:         receivedAt || null,
        zelle_reference:           reference.trim() || null,
        zelle_verification_status: status,
        zelle_payment_notes:       notes.trim() || null,
      };
      await api.patch(`/admin/orders/${order.public_id}/payment-reconciliation`, payload);
      toast.success("Zelle payment details saved.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [saving, senderName, amountRcv, receivedAt, reference, status, notes, order?.public_id, onSaved]);

  // Phase 1-ops · One atomic action that synchronises the legacy
  // payment.verified_at AND payment_reconciliation.zelle_verification_status
  // (Safeguard 2). No-op repeat writes no duplicate audit event.
  const markVerified = useCallback(async () => {
    if (saving) return;
    if (!window.confirm(
      `Mark payment for order ${order.public_id} as VERIFIED?\n\n` +
      `This is an override that flags the payment as accepted even when ` +
      `sender name or amount differs. The original order total will NOT change. ` +
      `A before/after audit entry will be recorded.`
    )) return;
    setSaving(true);
    try {
      const payload = {
        reason: "Admin override — mark verified anyway",
        amount_received:  amountRcv === "" ? undefined : Number(amountRcv),
        zelle_sender_name: senderName.trim() || undefined,
        internal_notes:    notes.trim() || undefined,
      };
      const r = await api.post(`/admin/orders/${order.public_id}/payment/mark-verified`, payload);
      if (r?.data?.noop) {
        toast.info("Payment is already verified — no change was made.");
      } else {
        toast.success("Payment marked verified.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [saving, senderName, amountRcv, notes, order?.public_id, onSaved]);

  return (
    <div
      className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]"
      data-testid="zelle-verification-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={18} className="text-emerald-700" />
        <h3 className="font-display text-lg text-[var(--ink)]">Zelle Payment Verification</h3>
        <span
          className={`ml-auto inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${toneClass(statusTone(rec.zelle_verification_status || "not_reviewed"))}`}
          data-testid="zelle-status-current"
        >
          {STATUS_OPTIONS.find((o) => o.value === (rec.zelle_verification_status || "not_reviewed"))?.label || "Not reviewed"}
        </span>
      </div>

      {/* Side-by-side comparison */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="border border-neutral-300 rounded-lg p-3 bg-neutral-50">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-2">
            Order (immutable)
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-[var(--smoke)]">Customer</dt>
            <dd className="font-semibold" data-testid="zelle-order-customer">{customerName}</dd>
            <dt className="text-[var(--smoke)]">Email</dt>
            <dd className="font-mono text-xs break-all">{customerEmail}</dd>
            <dt className="text-[var(--smoke)]">Order total</dt>
            <dd className="font-mono font-bold" data-testid="zelle-order-total">{currency(orderTotal)}</dd>
          </dl>
        </div>
        <div className="border border-neutral-300 rounded-lg p-3 bg-white">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-2">
            Zelle payment (editable)
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-[var(--smoke)]">Name on Zelle</dt>
            <dd className="font-semibold" data-testid="zelle-name-preview">
              {senderName || <span className="italic opacity-60">—</span>}
            </dd>
            <dt className="text-[var(--smoke)]">Amount received</dt>
            <dd className="font-mono font-bold" data-testid="zelle-amount-preview">
              {amountRcv === "" ? <span className="italic opacity-60">—</span> : currency(amountRcv)}
            </dd>
            <dt className="text-[var(--smoke)]">Difference</dt>
            <dd
              className={`font-mono font-bold ${liveDiff === null ? "" : liveDiff === 0 ? "text-emerald-800" : "text-amber-800"}`}
              data-testid="zelle-difference-preview"
            >
              {formatDifference(liveDiff)}
            </dd>
          </dl>
        </div>
      </div>

      {(senderMismatch || amountMismatch) && (
        <div
          className="mb-4 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg px-3 py-2 text-sm text-amber-900 flex items-start gap-2"
          data-testid="zelle-mismatch-notice"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            {amountMismatch && (
              <div>
                Payment amount differs from order total by{" "}
                <span className="font-mono font-bold">{formatDifference(liveDiff)}</span>.
              </div>
            )}
            {senderMismatch && (
              <div>Zelle sender name differs from the customer&apos;s order name.</div>
            )}
            <div className="text-xs opacity-80 mt-1">
              Administrator confirmation is required before marking payment as matched.
            </div>
          </div>
        </div>
      )}

      {/* Edit grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <label className="text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Name shown on Zelle
          <input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="e.g. Maria Gonzalez"
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal"
            data-testid="zelle-sender-name-input"
          />
        </label>
        <label className="text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Zelle amount received
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountRcv}
            onChange={(e) => setAmountRcv(e.target.value)}
            placeholder="0.00"
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal font-mono"
            data-testid="zelle-amount-input"
          />
        </label>
        <label className="text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Zelle payment received
          <input
            type="datetime-local"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal"
            data-testid="zelle-received-at-input"
          />
          {receivedAt && (
            <span className="text-[10px] font-normal tracking-normal opacity-70">
              {detailLong(receivedAt)}
            </span>
          )}
        </label>
        <label className="text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Zelle confirmation or reference
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Optional"
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal font-mono"
            data-testid="zelle-reference-input"
          />
        </label>
        <label className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Verification status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal"
            data-testid="zelle-status-select"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-[var(--ink)] flex flex-col gap-1">
          Payment notes
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Customer sent $7.00 for a $5.35 order and confirmed the difference was intentional."
            rows={3}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm normal-case font-normal tracking-normal"
            data-testid="zelle-notes-input"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center justify-end gap-3 flex-wrap">
        {dirty && (
          <span className="text-xs italic text-[var(--smoke)]">Unsaved changes</span>
        )}
        <button
          type="button"
          onClick={markVerified}
          disabled={saving}
          className="btn-red text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="zelle-mark-verified-button"
          title="Atomically set payment.verified_at and zelle_verification_status='matched'. Idempotent."
        >
          <ShieldCheck size={14} /> Mark verified anyway
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="btn-yellow text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="zelle-save-button"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save payment details"}
        </button>
      </div>
    </div>
  );
}
